/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import {
  type UseMutationResult,
  type UseQueryResult,
  useMutation,
  useQueryClient,
  useQuery,
} from '@tanstack/react-query';

import { type Collaborator, collaboratorsApi } from '../api/collaborators.js';

const key = (planId: string): readonly unknown[] => ['itps', planId, 'collaborators'];

export function useCollaborators(planId: string): UseQueryResult<readonly Collaborator[]> {
  return useQuery({
    queryKey: key(planId),
    queryFn: () => collaboratorsApi.list(planId),
    enabled: planId !== '',
  });
}

export function useAddCollaborator(
  planId: string,
): UseMutationResult<readonly Collaborator[], Error, string> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (therapistId: string) => collaboratorsApi.add(planId, therapistId),
    onSuccess: (rows) => {
      qc.setQueryData(key(planId), rows);
    },
  });
}

export function useRemoveCollaborator(
  planId: string,
): UseMutationResult<readonly Collaborator[], Error, string> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (therapistId: string) => collaboratorsApi.remove(planId, therapistId),
    onSuccess: (rows) => {
      qc.setQueryData(key(planId), rows);
    },
  });
}
